import { toast } from 'react-toastify';
import { JAVA_API_STATUS } from '../../utils/axios-interceptor';
import { fetchFilteredViews, createFilteredView, updateFilteredView } from './FilteredViewService';

export interface FilteredView {
    viewId: string;
    viewName: string;
    filters: any;
    columns?: string[];
    source: string;
    pnoId: string;
    createdBy: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
    userName?: string;
}

export interface CreateViewPayload {
    viewName: string;
    pnoId: string;
    source: string;
    filters: any;
    columns?: string[];
    userId: string;
}

export interface UpdateViewPayload {
    viewId: string;
    filters: any;
    userId: string;
    viewName?: string;
    columns?: string[];
}

// Helper function to map source for API calls
const mapSourceForAPI = (source: string): string => {
    return source === 'DEAL_VIEW' || source === 'MANDATE_VIEW' ? 'DEALS' : source;
};

export const getFilteredViews = async (payload: { pnoId: string; source: string }): Promise<FilteredView[] | false> => {
    try {
        const res = await fetchFilteredViews({
            ...payload,
            source: mapSourceForAPI(payload.source),
        });

        // Check if response exists and has proper structure
        if (!res || !res.data) {
            console.warn('Invalid API response structure for filtered views');
            return [];
        }

        if (res.data?.responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return res.data?.filteredViews || [];
        } else {
            const errorMessage = res.data?.responseData?.responseMessage || 'Failed to fetch filtered views';
            console.warn('API error fetching filtered views:', errorMessage);
            // Don't show toast for auth errors (401) as they're handled elsewhere
            if (res.status !== 401) {
                toast.error(errorMessage);
            }
            return [];
        }
    } catch (err: any) {
        console.error('Error fetching filtered views:', err);

        // Handle different error scenarios gracefully
        if (err.response?.data?.responseData?.responseCode === JAVA_API_STATUS.ERROR) {
            const errorMessage = err.response.data.responseData.responseMessage || 'Failed to fetch filtered views';
            // Don't show toast for auth errors (401) as they're handled elsewhere
            if (err.response?.status !== 401) {
                toast.error(errorMessage);
            }
        } else if (err.response?.status !== 401) {
            // Only show generic error for non-auth errors
            console.warn('Failed to fetch filtered views - non-auth error');
        }

        // Return empty array instead of false to prevent further errors
        return [];
    }
};

export const saveFilteredView = async (payload: CreateViewPayload): Promise<FilteredView | false> => {
    try {
        const res = await createFilteredView({
            ...payload,
            source: mapSourceForAPI(payload.source),
        });
        if (res.data?.responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return res.data?.data || false;
        } else {
            toast.error(res.data?.responseData?.responseMessage);
            return false;
        }
    } catch (err: any) {
        if (err.response?.data?.responseData?.responseCode === JAVA_API_STATUS.ERROR) {
            toast.error(err.response?.data?.responseData?.responseMessage);
            return false;
        }
        console.error('Error creating filtered view:', err);
        return false;
    }
};

export const editFilteredView = async (payload: UpdateViewPayload): Promise<FilteredView | false> => {
    try {
        const res = await updateFilteredView(payload);
        if (res.data?.responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return res.data?.data || false;
        } else {
            toast.error(res.data?.responseData?.responseMessage);
            return false;
        }
    } catch (err: any) {
        if (err.response?.data?.responseData?.responseCode === JAVA_API_STATUS.ERROR) {
            toast.error(err.response?.data?.responseData?.responseMessage);
            return false;
        }
        console.error('Error updating filtered view:', err);
        return false;
    }
};
